import { useState } from "react";
import {
  Box,
  Grid,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import SaveIcon from "@mui/icons-material/Save";
import { updateToastError } from "../utils/notification-loading";

// field definitions
const filterFields = [
  {
    field: "src_ip",
    headerName: "SOURCE IP",
    type: "text",
    operators: ["contains", "notContains", "equals", "notEquals"],
  },
  {
    field: "dest_ip",
    headerName: "DESTINATION IP",
    type: "text",
    operators: ["contains", "notContains", "equals", "notEquals"],
  },
  {
    field: "protocol",
    headerName: "INDUSTRIAL PROTOCOL",
    type: "text",
    operators: ["equals", "notEquals"],
  },
  {
    field: "transport",
    headerName: "TRANSPORT",
    type: "transport",
    operators: ["equals"],
  },
  {
    field: "src_port",
    headerName: "SOURCE PORT",
    type: "number",
    operators: ["equals", "gt", "lt", "between"],
  },
  {
    field: "dest_port",
    headerName: "DEST PORT",
    type: "number",
    operators: ["equals", "gt", "lt", "between"],
  },
  {
    field: "is_write",
    headerName: "IS WRITE",
    type: "boolean",
    operators: ["equals"],
  },
  {
    field: "operation",
    headerName: "OPERATION",
    type: "text",
    operators: ["contains", "notContains", "equals", "notEquals"],
  },
  {
    field: "address",
    headerName: "ADDRESS",
    type: "text",
    operators: ["contains", "equals", "notEquals"],
  },
  {
    field: "data_type",
    headerName: "DATA TYPE",
    type: "text",
    operators: ["contains", "equals", "notEquals"],
  },
  {
    field: "value",
    headerName: "VALUE",
    type: "text",
    operators: ["contains", "equals", "notEquals"],
  },
  {
    field: "quantity",
    headerName: "QUANTITY",
    type: "number",
    operators: ["equals", "gt", "lt", "between"],
  },
  {
    field: "status",
    headerName: "STATUS",
    type: "text",
    operators: ["equals", "notEquals"],
  },
];

const operatorLabels = {
  contains: "contains",
  notContains: "not contains",
  equals: "equals",
  notEquals: "not equals",
  gt: ">",
  lt: "<",
  between: "between",
};

function defaultValue(fieldDef) {
  if (fieldDef.type === "boolean") return true;
  if (fieldDef.type === "transport") return "tcp";
  if (fieldDef.type === "number") return "";
  return "";
}

function getFiltersWithoutLogic(list) {
  if (list.length > 0 && list[0].logic) {
    return list.map(({ logic, ...rest }) => rest);
  }
  return list;
}

const ValueInput = ({ filter, index, onChange }) => {
  const fieldDef = filterFields.find((f) => f.field === filter.field);
  if (!fieldDef) return null;

  if (fieldDef.type === "boolean") {
    return (
      <Select
        variant="standard"
        fullWidth
        value={filter.value === true || filter.value === "true" ? true : false}
        onChange={(e) => onChange(index, "value", e.target.value === "true" || e.target.value === true)}
      >
        <MenuItem value={true}>TRUE</MenuItem>
        <MenuItem value={false}>FALSE</MenuItem>
      </Select>
    );
  }

  if (fieldDef.type === "transport") {
    return (
      <Select
        variant="standard"
        fullWidth
        value={filter.value || "tcp"}
        onChange={(e) => onChange(index, "value", e.target.value)}
      >
        <MenuItem value="tcp">TCP</MenuItem>
        <MenuItem value="udp">UDP</MenuItem>
      </Select>
    );
  }

  if (fieldDef.type === "number") {
    if (filter.operator === "between") {
      const [from, to] = Array.isArray(filter.value) ? filter.value : ["", ""];
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            placeholder="from"
            variant="standard"
            type="number"
            sx={{ flex: 1 }}
            value={from}
            onChange={(e) => onChange(index, "value", [e.target.value, to])}
          />
          <TextField
            placeholder="to"
            variant="standard"
            type="number"
            sx={{ flex: 1 }}
            value={to}
            onChange={(e) => onChange(index, "value", [from, e.target.value])}
          />
        </Box>
      );
    }
    return (
      <TextField
        variant="standard"
        type="number"
        fullWidth
        placeholder={fieldDef.headerName}
        value={filter.value || ""}
        onChange={(e) => onChange(index, "value", e.target.value)}
      />
    );
  }

  // text
  const isIp = fieldDef.field === "src_ip" || fieldDef.field === "dest_ip";
  const isValidIp = (ip) => /^([0-9.]+,?)*$/.test(ip);
  return (
    <TextField
      variant="standard"
      fullWidth
      placeholder={fieldDef.headerName}
      value={filter.value || ""}
      onChange={(e) => onChange(index, "value", e.target.value)}
      error={isIp && !!filter.value && !isValidIp(filter.value)}
      helperText={isIp && filter.value && !isValidIp(filter.value) ? "Invalid IP" : ""}
    />
  );
};

const OtCustomFilters = ({ filterModel, setFilterModel, mode = null }) => {
  const availableFields = mode === "r" || mode === "w"
    ? filterFields.filter((f) => f.field !== "is_write")
    : filterFields;

  const [filtersList, setFiltersList] = useState(getFiltersWithoutLogic(filterModel));

  const handleChange = (index, key, value) => {
    const updated = [...filtersList];
    if (key === "field") {
      const fieldDef = filterFields.find((f) => f.field === value);
      updated[index] = {
        ...updated[index],
        field: value,
        operator: fieldDef.operators[0],
        value: defaultValue(fieldDef),
      };
    } else if (key === "operator") {
      const fieldDef = filterFields.find((f) => f.field === updated[index].field);
      const newVal =
        value === "between"
          ? ["", ""]
          : fieldDef?.type === "number"
          ? ""
          : updated[index].value;
      updated[index] = { ...updated[index], operator: value, value: newVal };
    } else {
      updated[index] = { ...updated[index], [key]: value };
    }
    setFiltersList(updated);
  };

  const handleAddFilter = () => {
    const fieldDef = availableFields[0];
    const newFilter = {
      field: fieldDef.field,
      operator: fieldDef.operators[0],
      value: defaultValue(fieldDef),
    };
    if (filtersList.length > 0) newFilter.logic = "and";
    setFiltersList((prev) => [...prev, newFilter]);
  };

  const handleDelete = (index) => {
    let newList = filtersList.filter((_, i) => i !== index);
    if (index === 0 && newList.length > 0) {
      const { logic, ...rest } = newList[0];
      newList[0] = rest;
    }
    setFiltersList(newList);
  };

  const handleRemoveAll = () => {
    setFiltersList([]);
    setFilterModel([]);
  };

  const handleSave = () => {
    for (const f of filtersList) {
      const empty =
        f.value === null ||
        f.value === undefined ||
        f.value === "" ||
        (Array.isArray(f.value) &&
          (f.value.length === 0 || f.value.some((v) => v === "")));
      if (empty) {
        updateToastError("Invalid filter value");
        return;
      }
    }
    setFilterModel(filtersList);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "55vw" }}>
      {filtersList.length > 0 ? (
        filtersList.map((filter, index) => {
          const fieldDef = filterFields.find((f) => f.field === filter.field);
          return (
            <Box key={index} sx={{ width: "100%" }}>
              <Grid container spacing={2} alignItems="center" sx={{ padding: 2 }}>
                {/* delete */}
                <Grid size={0.5}>
                  <IconButton color="error" onClick={() => handleDelete(index)} sx={{ minWidth: 0, padding: "6px" }}>
                    <ClearIcon />
                  </IconButton>
                </Grid>

                {/* logic */}
                {filter.logic ? (
                  <Grid size={1.2}>
                    <Select variant="standard" fullWidth value={filter.logic} onChange={(e) => handleChange(index, "logic", e.target.value)}>
                      <MenuItem value="and">AND</MenuItem>
                      <MenuItem value="or">OR</MenuItem>
                    </Select>
                  </Grid>
                ) : filtersList.length > 1 ? (
                  <Grid size={1.2} />
                ) : null}

                {/* field */}
                <Grid size={filtersList.length > 1 ? 2.8 : 3.5}>
                  <Select variant="standard" fullWidth value={filter.field || ""} onChange={(e) => handleChange(index, "field", e.target.value)}>
                    {availableFields.map((f) => (
                      <MenuItem key={f.field} value={f.field}>{f.headerName}</MenuItem>
                    ))}
                  </Select>
                </Grid>

                {/* operator */}
                <Grid size={2}>
                  <Select variant="standard" fullWidth value={filter.operator || ""} onChange={(e) => handleChange(index, "operator", e.target.value)}>
                    {(fieldDef?.operators || []).map((op) => (
                      <MenuItem key={op} value={op}>{operatorLabels[op] ?? op}</MenuItem>
                    ))}
                  </Select>
                </Grid>

                {/* value */}
                <Grid size={filter.operator === "between" ? 5 : 5}>
                  <ValueInput filter={filter} index={index} onChange={handleChange} />
                </Grid>
              </Grid>
            </Box>
          );
        })
      ) : (
        <Typography sx={{ fontSize: 16, fontFamily: "poppins", py: 2 }}>No filters</Typography>
      )}

      <Grid container spacing={2} display="flex" justifyContent="space-between" alignItems="center" sx={{ padding: 1, width: "100%" }}>
        <Grid size={4}>
          <Button fullWidth sx={{ fontFamily: "poppins", fontSize: 12 }} onClick={handleAddFilter}>
            <PlaylistAddIcon sx={{ marginRight: 1 }} /> ADD FILTER
          </Button>
        </Grid>
        <Grid size={4} display="flex" gap={2} justifyContent="flex-end">
          <Button
            color="error"
            variant="contained"
            disabled={filtersList.length === 0}
            fullWidth
            onClick={handleRemoveAll}
            sx={{ fontFamily: "poppins", fontSize: 12, "&:hover": { backgroundColor: "#f44336 !important", opacity: 0.8 } }}
          >
            <DeleteForeverIcon fontSize="small" sx={{ marginRight: 1 }} /> REMOVE ALL
          </Button>
          <Button fullWidth sx={{ fontFamily: "poppins", fontSize: 12 }} onClick={handleSave}>
            <SaveIcon fontSize="small" sx={{ marginRight: 1 }} /> APPLY FILTER
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OtCustomFilters;
